import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Plus, Power } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const BLANK = { code: "", kind: "percent", value: "", max_uses: "", expires: "", note: "" };
const off = (p) => (p.kind === "percent" ? `${p.value}% off` : `S$${Number(p.value).toFixed(2)} off`);

export const PromoSection = ({ call }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(BLANK);
  const load = useCallback(() => call("/promos").then(setRows).catch((x) => toast.error(x.message)), [call]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.toUpperCase() : e.target.value });
  const create = async (e) => {
    e.preventDefault();
    try {
      const p = await call("/promos", "POST", { ...f, value: Number(f.value), max_uses: Number(f.max_uses || 0) });
      toast.success(`${p.code} created · Pro costs S$${p.price.toFixed(2)} with it`);
      setF(BLANK); load();
    } catch (x) { toast.error(x.message); }
  };
  const toggle = async (code) => { try { await call(`/promos/${code}/toggle`, "POST"); load(); } catch (x) { toast.error(x.message); } };
  return (
    <section className="lw-card p-5 space-y-4" data-testid="admin-promos">
      <h2 className="font-semibold flex items-center gap-2"><Tag className="h-4 w-4" />Promo codes</h2>
      <form onSubmit={create} className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        <Input value={f.code} onChange={set("code")} placeholder="CODE" maxLength={20} pattern="[A-Za-z0-9-]{3,20}" required className="font-mono sm:col-span-2" data-testid="promo-new-code" />
        <select value={f.kind} onChange={set("kind")} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="promo-new-kind">
          <option value="percent">% off</option><option value="amount">S$ off</option>
        </select>
        <Input type="number" min="0.01" step="0.01" value={f.value} onChange={set("value")} placeholder={f.kind === "percent" ? "e.g. 20" : "e.g. 3.00"} required data-testid="promo-new-value" />
        <Input type="number" min="0" step="1" value={f.max_uses} onChange={set("max_uses")} placeholder="Max uses" title="0 or empty = unlimited" data-testid="promo-new-max" />
        <Input type="date" value={f.expires} onChange={set("expires")} title="Last valid day (optional)" data-testid="promo-new-expires" />
        <Input value={f.note} onChange={set("note")} placeholder="Note (optional)" className="col-span-2 sm:col-span-5" data-testid="promo-new-note" />
        <Button type="submit" className="col-span-2 sm:col-span-1" data-testid="promo-create-btn"><Plus className="h-4 w-4 mr-1" />Create</Button>
      </form>
      <p className="text-[11px] text-muted-foreground">100% off gives a free licence without payment. Codes work for Pro checkout by card, PayNow, PayPal and QR orders.</p>
      <div className="divide-y text-sm">
        {rows.map((p) => (
          <div key={p.code} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`promo-row-${p.code}`}>
            <span className="font-mono font-semibold">{p.code}</span><span>{off(p)}</span>
            <span className="text-muted-foreground text-xs">{p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ""} used{p.expires ? ` · until ${p.expires}` : ""}{p.note ? ` · ${p.note}` : ""}</span>
            <span className="flex-1" />
            <span className={`text-xs rounded px-1.5 py-0.5 ${p.active ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{p.active ? "Active" : "Off"}</span>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => toggle(p.code)} title={p.active ? "Turn off" : "Turn on"} data-testid={`promo-toggle-${p.code}`}><Power className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        {!rows.length && <p className="text-muted-foreground py-2">No promo codes yet.</p>}
      </div>
    </section>
  );
};
